import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listItemLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
}));

const UserDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const userDoc = doc(firestore, 'users', id);
      const userSnapshot = await getDoc(userDoc);
      
      if (userSnapshot.exists()) {
        setUser({
          id: userSnapshot.id,
          ...userSnapshot.data()
        });
      } else {
        // Si l'utilisateur n'existe pas, utiliser des données de démonstration
        setUser({
          id: id,
          name: 'John Doe',
          email: 'john.doe@example.com',
          role: 'Admin',
          status: 'Active',
          phone: '+33123456789',
          address: '123 Rue de Paris, 75001 Paris',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          permissions: ['users.read', 'users.write', 'companies.read', 'companies.write']
        });
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'utilisateur',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/users/${id}/edit`);
  };

  const handleDelete = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'users', id));
      
      setSnackbar({
        open: true,
        message: 'Utilisateur supprimé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/users');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de l\'utilisateur',
        severity: 'error'
      });
    }
  };

  const handleBack = () => {
    navigate('/admin/users');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'primary';
      case 'Inactive':
        return 'default';
      case 'Suspended':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Détails de l'utilisateur
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : user ? (
        <>
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" component="h2">
                  {user.name}
                </Typography>
                <Chip 
                  label={user.status} 
                  color={getStatusColor(user.status)} 
                  className={classes.chip}
                />
                <Chip 
                  label={user.role} 
                  variant="outlined" 
                  className={classes.chip}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations personnelles
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Email:</Box>
                          <Box>{user.email}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Téléphone:</Box>
                          <Box>{user.phone || 'Non renseigné'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Adresse:</Box>
                          <Box>{user.address || 'Non renseignée'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations du compte
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>ID:</Box>
                          <Box>{user.id}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Créé le:</Box>
                          <Box>{formatDate(user.createdAt)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Dernière connexion:</Box>
                          <Box>{formatDate(user.lastLogin)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              {user.permissions && user.permissions.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Permissions
                  </Typography>
                  <Box>
                    {user.permissions.map((permission, index) => (
                      <Chip 
                        key={index}
                        label={permission} 
                        variant="outlined" 
                        size="small"
                        className={classes.chip}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Supprimer
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Modifier
              </Button>
            </div>
          </Paper>
        </>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Utilisateur non trouvé
          </Typography>
        </Paper>
      )}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetailPage;
